import { useEffect, useMemo, useRef, useState } from 'react'
import {
  Check,
  FileText,
  ImageIcon,
  MoreVertical,
  Pencil,
  Plus,
  Trash2,
  Upload,
} from 'lucide-react'
import { SopWorkspace } from '@/components/files/SopWorkspace'
import { SopViewer } from '@/components/files/SopViewer'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { KpiCard } from '@/components/layout/KpiCard'
import { Badge } from '@/components/ui/Badge'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { PageError, PageLoading } from '@/components/ui/PageAsyncState'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { ImageHoverPreview } from '@/components/ui/ImageHoverPreview'
import { FormField } from '@/components/forms/FormField'
import { DateInput } from '@/components/forms/DateInput'
import { ProtoExcelTable, type ProtoColumn } from '@/components/table/ProtoExcelTable'
import { computeCatalogKpis } from '@/features/catalog/catalogMetrics'
import { formatDateFull } from '@/lib/dateFormat'
import { computeExpirationDate } from '@/lib/expirationDate'
import {
  isImageName,
  isPdfName,
  readFileAsDataUrl,
  uniqueName,
} from '@/lib/files'
import {
  ATTRIBUTE_KIND_LABEL,
  COLOR_TYPE_OPTIONS,
  catalogService,
  type AttributeKind,
  type CatalogItem,
  type CatalogSopFile,
} from '@/services/catalog'

// Bỏ thuộc tính Veneer — chỉ còn Màu và Gỗ
const TABS: AttributeKind[] = ['color', 'wood']

type FormState = {
  code: string
  name: string
  note: string
  type: string
  signedDate: string
  sopFiles: CatalogSopFile[]
  isActive?: boolean
}

const emptyForm = (): FormState => ({
  code: '',
  name: '',
  note: '',
  type: '',
  signedDate: '',
  sopFiles: [],
  isActive: true,
})

function RowActionsMenu({
  onEdit,
  onDelete,
}: {
  onEdit: () => void
  onDelete: () => void
}) {
  const [open, setOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false)
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
      document.addEventListener('keydown', handleKeyDown)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [open])

  return (
    <div className="relative inline-flex items-center justify-end" ref={menuRef}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation()
          setOpen((prev) => !prev)
        }}
        className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors cursor-pointer"
        title="Thao tác"
      >
        <MoreVertical className="h-4 w-4" />
      </button>

      {open && (
        <div className="absolute right-0 top-full z-30 mt-1 w-32 origin-top-right rounded-md border border-slate-200 bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setOpen(false)
              onEdit()
            }}
            className="flex w-full items-center px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 hover:text-sky-600 transition-colors cursor-pointer"
          >
            <Pencil className="mr-2 h-3.5 w-3.5 text-slate-400" />
            Sửa
          </button>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setOpen(false)
              onDelete()
            }}
            className="flex w-full items-center px-3 py-1.5 text-xs font-medium text-rose-600 hover:bg-rose-50 transition-colors cursor-pointer"
          >
            <Trash2 className="mr-2 h-3.5 w-3.5 text-rose-400" />
            Xóa
          </button>
        </div>
      )}
    </div>
  )
}

export function CatalogPage() {
  const [kind, setKind] = useState<AttributeKind>('color')
  const [items, setItems] = useState<CatalogItem[]>([])
  const [loading, setLoading] = useState(true)
  const [sideForm, setSideForm] = useState<FormState>(emptyForm)
  const [showSidePanel, setShowSidePanel] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  // Inline edit state
  const [editingRowId, setEditingRowId] = useState<string | null>(null)
  const [inlineDraft, setInlineDraft] = useState<FormState>(emptyForm)

  const [preview, setPreview] = useState<{
    files: CatalogSopFile[]
    activeId: string
  } | null>(null)

  async function reload(nextKind = kind) {
    setLoading(true)
    setError(null)
    try {
      const rows = await catalogService.list(nextKind, true)
      setItems(rows)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Không tải được danh mục',
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    setEditingRowId(null)
    setSideForm(emptyForm())
    setInlineDraft(emptyForm())
    setError(null)
    void reload(kind)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind])

  const kpis = useMemo(() => computeCatalogKpis(items), [items])

  function startInlineEdit(item: CatalogItem) {
    setEditingRowId(item.id)
    setInlineDraft({
      code: item.code,
      name: item.name,
      note: item.note ?? '',
      type: item.type ?? '',
      signedDate: item.signedDate ?? '',
      sopFiles: item.sopFiles ? [...item.sopFiles] : [],
      isActive: item.isActive,
    })
  }

  function startInlineAdd() {
    setEditingRowId('__new__')
    setInlineDraft({
      code: '',
      name: '',
      note: '',
      type: kind === 'color' ? 'Pingment' : '',
      signedDate: new Date().toISOString().slice(0, 10),
      sopFiles: [],
      isActive: true,
    })
  }

  function cancelInlineEdit() {
    setEditingRowId(null)
    setInlineDraft(emptyForm())
  }

  async function saveInlineEdit() {
    if (!inlineDraft.code.trim()) {
      alert('Vui lòng nhập Mã')
      return
    }
    if (!inlineDraft.name.trim()) {
      alert('Vui lòng nhập Tên')
      return
    }

    setSaving(true)
    setError(null)
    try {
      const payload = {
        code: inlineDraft.code.trim().toUpperCase(),
        name: inlineDraft.name.trim(),
        note: inlineDraft.note.trim(),
        type: inlineDraft.type || null,
        signedDate: inlineDraft.signedDate || null,
        sopFiles: inlineDraft.sopFiles,
        isActive: inlineDraft.isActive ?? true,
      }

      if (editingRowId === '__new__') {
        await catalogService.create(kind, payload)
      } else if (editingRowId) {
        await catalogService.update(kind, editingRowId, payload)
      }
      cancelInlineEdit()
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể lưu dòng')
    } finally {
      setSaving(false)
    }
  }

  async function handleSideFormSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSaving(true)
    try {
      const payload = {
        code: sideForm.code.trim().toUpperCase(),
        name: sideForm.name.trim(),
        note: sideForm.note.trim(),
        type: sideForm.type || null,
        signedDate: sideForm.signedDate || null,
        sopFiles: sideForm.sopFiles,
        isActive: true,
      }
      await catalogService.create(kind, payload)
      setSideForm(emptyForm())
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể tạo mới')
    } finally {
      setSaving(false)
    }
  }

  async function toggleActive(item: CatalogItem) {
    setError(null)
    try {
      await catalogService.update(kind, item.id, {
        code: item.code,
        name: item.name,
        note: item.note,
        type: item.type,
        signedDate: item.signedDate,
        sopFiles: item.sopFiles,
        isActive: !item.isActive,
      })
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể cập nhật trạng thái')
    }
  }

  async function handleArchive(item: CatalogItem) {
    if (
      !window.confirm(
        `Xác nhận xóa/ngưng dùng "${item.code} — ${item.name}"?`,
      )
    ) {
      return
    }
    setError(null)
    try {
      await catalogService.remove(kind, item.id)
      if (editingRowId === item.id) cancelInlineEdit()
      await reload()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Không thể xóa/ngưng dùng')
    }
  }

  async function handleInlineFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const list = e.target.files
    if (!list?.length) return
    try {
      const existingNames = inlineDraft.sopFiles.map((f) => f.name)
      const next: CatalogSopFile[] = []
      for (const file of Array.from(list)) {
        if (!isImageName(file.name) && !isPdfName(file.name)) {
          alert('Chỉ nhận file ảnh (PNG/JPG/…) hoặc file PDF.')
          continue
        }
        const name = uniqueName(
          [...existingNames, ...next.map((f) => f.name)],
          file.name,
        )
        const dataUrl = await readFileAsDataUrl(file)
        next.push({
          id: crypto.randomUUID(),
          name,
          dataUrl,
          uploadedAt: new Date().toISOString(),
          size: file.size,
        })
      }
      if (next.length) {
        setInlineDraft((prev) => ({
          ...prev,
          sopFiles: [...prev.sopFiles, ...next],
        }))
      }
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Không tải được file')
    } finally {
      e.target.value = ''
    }
  }

  // Prepared rows: if adding new inline, prepend dummy item
  const displayRows = useMemo(() => {
    if (editingRowId === '__new__') {
      const newDummy: CatalogItem = {
        id: '__new__',
        code: inlineDraft.code || '',
        name: inlineDraft.name || '',
        note: inlineDraft.note || '',
        type: inlineDraft.type || '',
        signedDate: inlineDraft.signedDate || '',
        sopFiles: inlineDraft.sopFiles,
        isActive: inlineDraft.isActive ?? true,
      }
      return [newDummy, ...items]
    }
    return items
  }, [editingRowId, inlineDraft, items])

  const columns: ProtoColumn<CatalogItem>[] = useMemo(
    () => [
      {
        id: 'code',
        header: 'MÃ',
        sortable: true,
        hideable: false,
        width: 100,
        cell: (item) => {
          if (editingRowId === item.id) {
            return (
              <input
                value={inlineDraft.code}
                onChange={(e) =>
                  setInlineDraft((prev) => ({
                    ...prev,
                    code: e.target.value.toUpperCase(),
                  }))
                }
                placeholder="Mã..."
                className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs font-mono font-semibold outline-none focus:border-sky-500"
                autoFocus
              />
            )
          }
          return (
            <span className="font-mono text-xs font-semibold text-slate-700">
              {item.code}
            </span>
          )
        },
        sortValue: (item) => item.code,
      },
      {
        id: 'name',
        header: 'TÊN',
        sortable: true,
        width: 170,
        cell: (item) => {
          if (editingRowId === item.id) {
            return (
              <input
                value={inlineDraft.name}
                onChange={(e) =>
                  setInlineDraft((prev) => ({
                    ...prev,
                    name: e.target.value,
                  }))
                }
                placeholder="Tên..."
                className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs outline-none focus:border-sky-500"
              />
            )
          }
          // Chỉ hiện tên, không có bảng màu phía trước tên
          return (
            <span className="font-medium text-[var(--color-text)]">
              {item.name}
            </span>
          )
        },
        sortValue: (item) => item.name,
      },
      {
        id: 'note',
        header: 'GHI CHÚ',
        cell: (item) => {
          if (editingRowId === item.id) {
            return (
              <input
                value={inlineDraft.note}
                onChange={(e) =>
                  setInlineDraft((prev) => ({
                    ...prev,
                    note: e.target.value,
                  }))
                }
                placeholder="Ghi chú..."
                className="w-full rounded border border-slate-300 bg-white px-2 py-1 text-xs outline-none focus:border-sky-500"
              />
            )
          }
          return item.note ? (
            <span className="line-clamp-2 text-xs text-slate-600">{item.note}</span>
          ) : (
            <span className="text-slate-300">—</span>
          )
        },
        sortValue: (item) => item.note ?? '',
      },
      {
        id: 'type',
        header: 'LOẠI',
        sortable: true,
        width: 125,
        cell: (item) => {
          if (editingRowId === item.id) {
            return (
              <select
                value={inlineDraft.type || ''}
                onChange={(e) =>
                  setInlineDraft((prev) => ({
                    ...prev,
                    type: e.target.value,
                  }))
                }
                className="w-full rounded border border-slate-300 bg-white px-1.5 py-1 text-xs outline-none focus:border-sky-500 font-medium"
              >
                <option value="">— Chọn loại —</option>
                {COLOR_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            )
          }
          if (!item.type) {
            return <span className="text-slate-300">—</span>
          }
          const isStain = item.type.toLowerCase().includes('stain')
          return (
            <span
              className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${
                isStain
                  ? 'bg-amber-50 text-amber-800 border border-amber-200'
                  : 'bg-sky-50 text-sky-800 border border-sky-200'
              }`}
            >
              {item.type}
            </span>
          )
        },
        sortValue: (item) => item.type ?? '',
      },
      {
        id: 'signedDate',
        header: 'NGÀY KÝ',
        sortable: true,
        width: 120,
        cell: (item) => {
          if (editingRowId === item.id) {
            return (
              <DateInput
                value={inlineDraft.signedDate || ''}
                onChange={(val) =>
                  setInlineDraft((prev) => ({
                    ...prev,
                    signedDate: val,
                  }))
                }
                className="w-full text-xs font-mono"
              />
            )
          }
          return item.signedDate ? (
            <span className="font-mono text-xs text-slate-700">
              {formatDateFull(item.signedDate)}
            </span>
          ) : (
            <span className="text-slate-300">—</span>
          )
        },
        sortValue: (item) => item.signedDate ?? '',
      },
      {
        id: 'expirationDate',
        header: 'NGÀY HẾT HẠN',
        sortable: true,
        width: 140,
        cell: (item) => {
          const targetSignedDate =
            editingRowId === item.id ? inlineDraft.signedDate : item.signedDate
          const { formattedDate, status } = computeExpirationDate(targetSignedDate)

          if (status === 'none') {
            return <span className="text-slate-300">—</span>
          }
          if (status === 'expired') {
            return <Badge tone="danger">{formattedDate}</Badge>
          }
          if (status === 'warning') {
            return <Badge tone="warning">{formattedDate}</Badge>
          }
          return (
            <span className="font-mono text-xs text-slate-700">
              {formattedDate}
            </span>
          )
        },
        sortValue: (item) =>
          computeExpirationDate(item.signedDate).expirationDate ?? '',
      },
      {
        id: 'sop',
        header: 'SOP',
        width: 170,
        cell: (item) => {
          if (editingRowId === item.id) {
            return (
              <div className="flex flex-col gap-1 py-0.5">
                {inlineDraft.sopFiles && inlineDraft.sopFiles.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {inlineDraft.sopFiles.map((f) => (
                      <div
                        key={f.id}
                        className="inline-flex items-center gap-1 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] border border-slate-200"
                      >
                        <button
                          type="button"
                          onClick={() => setPreview({ files: inlineDraft.sopFiles, activeId: f.id })}
                          className="flex items-center gap-1 text-sky-600 hover:underline max-w-[90px] truncate"
                          title="Xem trực tiếp file"
                        >
                          {isImageName(f.name) ? (
                            <ImageIcon className="h-3 w-3 shrink-0" />
                          ) : (
                            <FileText className="h-3 w-3 shrink-0" />
                          )}
                          <span className="truncate">{f.name}</span>
                        </button>
                        <button
                          type="button"
                          onClick={() =>
                            setInlineDraft((prev) => ({
                              ...prev,
                              sopFiles: prev.sopFiles.filter((x) => x.id !== f.id),
                            }))
                          }
                          className="text-slate-400 hover:text-rose-500 font-bold px-0.5"
                          title="Xóa file này"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                ) : null}
                <label className="inline-flex cursor-pointer items-center justify-center gap-1 rounded border border-dashed border-slate-300 bg-slate-50 hover:bg-slate-100 px-2 py-1 text-xs text-slate-600 transition-colors">
                  <Upload className="h-3 w-3 text-slate-400" />
                  <span>Tải file vào ô</span>
                  <input
                    type="file"
                    multiple
                    accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                    className="hidden"
                    onChange={handleInlineFileUpload}
                  />
                </label>
              </div>
            )
          }

          const sops = item.sopFiles ?? []
          if (!sops.length) {
            return <span className="text-xs text-slate-300">—</span>
          }
          return (
            <div className="flex flex-wrap items-center gap-1">
              {sops.map((f) => {
                const isImg = isImageName(f.name)
                const chip = (
                  <button
                    type="button"
                    className="inline-flex max-w-[140px] items-center gap-1 truncate rounded bg-slate-100 hover:bg-sky-50 hover:text-sky-700 px-1.5 py-0.5 text-xs font-medium text-slate-700 border border-slate-200 transition-colors"
                    onClick={(e) => {
                      e.stopPropagation()
                      setPreview({ files: sops, activeId: f.id })
                    }}
                    title={`Mở xem trực tiếp: ${f.name}`}
                  >
                    {isImg ? (
                      f.dataUrl ? (
                        <img
                          src={f.dataUrl}
                          alt=""
                          className="h-4 w-4 shrink-0 rounded-[2px] object-cover"
                        />
                      ) : (
                        <ImageIcon className="h-3 w-3 text-sky-500 shrink-0" />
                      )
                    ) : (
                      <FileText className="h-3 w-3 text-amber-500 shrink-0" />
                    )}
                    <span className="truncate">{f.name}</span>
                  </button>
                )
                return (
                  <ImageHoverPreview
                    key={f.id}
                    src={isImg && f.dataUrl ? f.dataUrl : undefined}
                    alt={f.name}
                  >
                    {chip}
                  </ImageHoverPreview>
                )
              })}
            </div>
          )
        },
        sortValue: (item) => (item.sopFiles ?? []).length,
      },
      {
        id: 'status',
        header: 'TRẠNG THÁI',
        sortable: true,
        width: 120,
        cell: (item) => {
          if (editingRowId === item.id) {
            return (
              <select
                value={inlineDraft.isActive ? 'active' : 'inactive'}
                onChange={(e) =>
                  setInlineDraft((prev) => ({
                    ...prev,
                    isActive: e.target.value === 'active',
                  }))
                }
                className="rounded border border-slate-300 bg-white px-2 py-1 text-xs font-medium outline-none focus:border-sky-500"
              >
                <option value="active">Đang sử dụng</option>
                <option value="inactive">Không sử dụng</option>
              </select>
            )
          }

          return (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation()
                void toggleActive(item)
              }}
              className="inline-flex cursor-pointer transition-transform active:scale-95"
              title={`Nhấp để chuyển sang ${item.isActive ? 'Không sử dụng' : 'Đang sử dụng'}`}
            >
              <StatusBadge
                tone={item.isActive ? 'success' : 'neutral'}
                label={item.isActive ? 'Đang sử dụng' : 'Không sử dụng'}
                showDot={true}
              />
            </button>
          )
        },
        sortValue: (item) => (item.isActive ? 1 : 0),
      },
      {
        id: 'actions',
        header: 'THAO TÁC',
        width: 100,
        align: 'right',
        cell: (item) => {
          if (editingRowId === item.id) {
            return (
              <div className="flex items-center justify-end gap-1">
                <button
                  type="button"
                  disabled={saving}
                  onClick={(e) => {
                    e.stopPropagation()
                    void saveInlineEdit()
                  }}
                  className="inline-flex items-center gap-1 rounded bg-sky-600 hover:bg-sky-700 px-2 py-1 text-xs font-semibold text-white shadow-xs transition-colors cursor-pointer"
                  title="Lưu dòng này"
                >
                  <Check className="h-3.5 w-3.5" />
                  Lưu
                </button>
                <button
                  type="button"
                  disabled={saving}
                  onClick={(e) => {
                    e.stopPropagation()
                    cancelInlineEdit()
                  }}
                  className="inline-flex items-center rounded border border-slate-300 bg-white hover:bg-slate-50 px-2 py-1 text-xs font-medium text-slate-600 transition-colors cursor-pointer"
                  title="Hủy sửa"
                >
                  Hủy
                </button>
              </div>
            )
          }

          return (
            <RowActionsMenu
              onEdit={() => startInlineEdit(item)}
              onDelete={() => void handleArchive(item)}
            />
          )
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [kind, editingRowId, inlineDraft, saving],
  )

  return (
    <div>
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Danh mục' },
        ]}
      />
      <PageHeader
        title="Danh mục thuộc tính"
        subtitle="Ghi chú master + SOP workspace (ảnh/PDF) — xem trực tiếp, tải xuống được."
      />

      {/* Tabs chuyển đổi giữa Màu và Gỗ (Đã bỏ Veneer) */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <div className="flex gap-2">
          {TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              onClick={() => setKind(tab)}
              className={`rounded-[var(--radius-sm)] px-3 py-1.5 text-sm font-medium transition-colors ${
                kind === tab
                  ? 'bg-[var(--color-accent)] text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {ATTRIBUTE_KIND_LABEL[tab]}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => setShowSidePanel((prev) => !prev)}
          className="text-xs font-medium text-slate-500 hover:text-slate-800 underline"
        >
          {showSidePanel ? 'Thu gọn form nhập bên trái' : 'Mở form nhập bên trái'}
        </button>
      </div>

      <div className="mb-3 grid grid-cols-2 gap-2 xl:grid-cols-4">
        <KpiCard label="Tổng" value={kpis.total} />
        <KpiCard label="Đang sử dụng" value={kpis.active} tone="success" />
        <KpiCard
          label="Không sử dụng"
          value={kpis.inactive}
          tone={kpis.inactive ? 'warning' : 'default'}
        />
      </div>

      {error ? (
        <PageError message={error} onRetry={() => void reload()} />
      ) : null}

      <div
        className={`grid grid-cols-1 gap-4 ${
          showSidePanel ? 'xl:grid-cols-[360px_1fr]' : 'grid-cols-1'
        }`}
      >
        {/* Form nhập nhanh bên trái (khi mở) */}
        {showSidePanel ? (
          <form
            onSubmit={handleSideFormSubmit}
            className="h-fit space-y-3 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-white p-4"
          >
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-semibold text-[var(--color-text)]">
                Thêm {ATTRIBUTE_KIND_LABEL[kind]}
              </h2>
              <button
                type="button"
                onClick={() => setShowSidePanel(false)}
                className="text-xs text-slate-400 hover:text-slate-600"
              >
                Đóng
              </button>
            </div>

            <FormField label="Mã nội bộ" required>
              <input
                required
                value={sideForm.code}
                onChange={(e) =>
                  setSideForm({ ...sideForm, code: e.target.value.toUpperCase() })
                }
                className="w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] px-3 py-2 text-sm font-mono outline-none focus:border-[var(--color-accent)]"
                placeholder="WAL / CRS / OAK..."
              />
            </FormField>
            <FormField label="Tên hiển thị" required>
              <input
                required
                value={sideForm.name}
                onChange={(e) => setSideForm({ ...sideForm, name: e.target.value })}
                className="w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] px-3 py-2 text-sm outline-none focus:border-[var(--color-accent)]"
                placeholder={`Tên ${ATTRIBUTE_KIND_LABEL[kind].toLowerCase()}...`}
              />
            </FormField>

            {kind === 'color' ? (
              <>
                <FormField label="Type">
                  <select
                    value={sideForm.type}
                    onChange={(e) =>
                      setSideForm({ ...sideForm, type: e.target.value })
                    }
                    className="w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm outline-none focus:border-[var(--color-accent)]"
                  >
                    <option value="">— Chọn type —</option>
                    {COLOR_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </FormField>
                <FormField label="Ngày ký">
                  <DateInput
                    value={sideForm.signedDate}
                    onChange={(val) =>
                      setSideForm({ ...sideForm, signedDate: val })
                    }
                    className="w-full text-sm font-mono"
                  />
                  {sideForm.signedDate ? (
                    <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
                      <span>Hết hạn:</span>
                      {(() => {
                        const exp = computeExpirationDate(sideForm.signedDate)
                        if (exp.status === 'expired') {
                          return <Badge tone="danger">{exp.formattedDate}</Badge>
                        }
                        if (exp.status === 'warning') {
                          return <Badge tone="warning">{exp.formattedDate}</Badge>
                        }
                        return (
                          <span className="font-mono text-slate-700">
                            {exp.formattedDate}
                          </span>
                        )
                      })()}
                    </div>
                  ) : null}
                </FormField>
              </>
            ) : null}

            <FormField label="Ghi chú (master)">
              <textarea
                rows={2}
                value={sideForm.note}
                onChange={(e) => setSideForm({ ...sideForm, note: e.target.value })}
                className="w-full rounded-[var(--radius-sm)] border border-[var(--color-border)] px-3 py-2 text-sm outline-none focus:border-[var(--color-accent)]"
                placeholder={
                  kind === 'color'
                    ? 'VD: quy trình sơn, bóng mờ, số lớp...'
                    : 'Ghi chú kỹ thuật...'
                }
              />
            </FormField>
            <FormField label="SOP workspace">
              <SopWorkspace
                files={sideForm.sopFiles}
                onChange={(sopFiles) => setSideForm({ ...sideForm, sopFiles })}
                editable
                allowDownload
              />
            </FormField>
            <div className="flex gap-2">
              <Button type="submit" disabled={saving} className="w-full justify-center">
                <Plus className="h-4 w-4" />
                Thêm {ATTRIBUTE_KIND_LABEL[kind]}
              </Button>
            </div>
          </form>
        ) : null}

        <div>
          {loading ? (
            <PageLoading />
          ) : !error && items.length === 0 && editingRowId !== '__new__' ? (
            <EmptyState
              title="Chưa có mục nào"
              description={`Nhấp nút "+ Thêm ${kind === 'color' ? 'màu' : 'gỗ'}" bên dưới để tạo dòng mới.`}
              action={
                <Button type="button" onClick={startInlineAdd} className="gap-1.5">
                  <Plus className="h-4 w-4" />
                  Thêm {kind === 'color' ? 'màu' : 'gỗ'}
                </Button>
              }
            />
          ) : !error ? (
            <ProtoExcelTable
              showRowNumbers
              columns={columns}
              rows={displayRows}
              rowKey={(r) => r.id}
              searchPlaceholder="Tìm mã, tên…"
              exportName={`danh-muc-${kind}`}
              pageSize={50}
              emptyMessage="Chưa có mục nào."
              stickyLeftColumnIds={['code', 'name']}
              stickyRightColumnIds={['actions']}
              toolbarExtra={
                <Button
                  type="button"
                  onClick={startInlineAdd}
                  disabled={editingRowId !== null || saving}
                  className="gap-1.5"
                >
                  <Plus className="h-4 w-4" />
                  Thêm {kind === 'color' ? 'màu' : 'dòng'} mới
                </Button>
              }
            />
          ) : null}
        </div>
      </div>

      {preview ? (
        <SopViewer
          files={preview.files}
          activeId={preview.activeId}
          onClose={() => setPreview(null)}
          onSelect={(id) => setPreview({ ...preview, activeId: id })}
          allowDownload
        />
      ) : null}
    </div>
  )
}
